import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { adminStorage } from "../../features/admin/service";
import { apiClient } from "../../lib/http/apiClient";
import { parseApiError } from "../../lib/http/apiErrors";
import PartnerOnboardingEditor from "../../components/admin/PartnerOnboardingEditor";
import PartnerShowcaseEditor from "../../components/admin/PartnerShowcaseEditor";
import RefreshButton from "../../components/admin/RefreshButton";
import type { PartnerRow } from "./AdminPartnersPage";
import { useAdminToast } from "../../context/AdminToastContext";

// Partner profile — cover banner + circular logo + all onboarding fields grouped
// by section (read view). "Edit profile" swaps in the shared questionnaire editor
// (PATCH /api/admin/partners/{owner}/profile). Header meta comes from the list row
// (router state), with a fallback fetch for deep links / refreshes.

const typeLabel = (t: string) => (t || "").replace(/_/g, " ");

interface Field { key: string; label: string; type: string; required: boolean; value?: any; }
interface Step { key: string; label: string; step: number; required: boolean; fields: Field[]; }
interface Questionnaire {
  partner_type: string | null;
  steps: Step[];
  status?: { completed: boolean; missing_steps: string[] };
}

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const isEmpty = (v: any) =>
  v == null || v === "" || (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

// Pull a dotted value ("brand_logo", "coverage.countries") from the questionnaire.
function fieldValue(q: Questionnaire | null, key: string): any {
  if (!q) return undefined;
  for (const s of q.steps) for (const f of s.fields) if (f.key === key) return f.value;
  return undefined;
}

// ── Read-only value renderer ─────────────────────────────────────────────────
function ReadValue({ f }: { f: Field }) {
  const v = f.value;
  if (isEmpty(v)) return <span className="text-gray-600 text-sm">—</span>;
  if (f.type === "media") {
    return <img src={String(v)} alt={f.label} className="h-16 w-16 rounded-lg object-cover border border-gray-700" />;
  }
  if (f.type === "bool") {
    return <span className="text-sm text-gray-200">{v ? "Yes" : "No"}</span>;
  }
  if (Array.isArray(v)) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {v.map((item, i) => (
          <span key={i} className="px-2 py-0.5 rounded-md bg-gray-800 border border-gray-700 text-gray-300 text-xs">{String(item)}</span>
        ))}
      </div>
    );
  }
  if (typeof v === "object") {
    return (
      <div className="space-y-0.5">
        {Object.entries(v).map(([k, val]) => (
          <div key={k} className="text-sm text-gray-200"><span className="text-gray-500">{k}: </span>{String(val)}</div>
        ))}
      </div>
    );
  }
  return <span className="text-sm text-gray-200 break-words">{String(v)}</span>;
}

export default function AdminPartnerProfilePage() {
  const { ownerId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stateRow = (location.state as { partner?: PartnerRow } | null)?.partner;

  const [meta, setMeta] = useState<PartnerRow | undefined>(stateRow);
  const [q, setQ] = useState<Questionnaire | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const { showError } = useAdminToast();
  useEffect(() => { if (err) showError(err); }, [err, showError]);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await apiClient.get<Questionnaire>(
        `/api/admin/partners/${ownerId}/onboarding`, { headers: adminHeaders() },
      );
      setQ(data);
      // Fallback: if we arrived without router state, resolve header meta from the list.
      if (!stateRow) {
        try {
          const list = await apiClient.get<PartnerRow[]>("/api/admin/partners", {
            params: { limit: 200 }, headers: adminHeaders(),
          });
          setMeta((Array.isArray(list.data) ? list.data : []).find((p) => p.owner_user_id === ownerId));
        } catch { /* header meta is best-effort */ }
      }
    } catch (e) {
      setErr(parseApiError(e).message);
    } finally {
      setLoading(false);
    }
  }, [ownerId, stateRow]);

  useEffect(() => { void load(); }, [load]);

  const orgName = fieldValue(q, "organization_name") || meta?.organization_name || "Partner";
  const brandName = fieldValue(q, "brand_name");
  const logo = fieldValue(q, "brand_logo");
  const banner = fieldValue(q, "channel_banner");
  const tagline = fieldValue(q, "tagline");
  const ptype = meta?.partner_type || q?.partner_type || "";

  const sections = useMemo(
    () => (q?.steps || []).filter((s) => (s.fields || []).length > 0),
    [q],
  );

  return (
    <div className="max-w-5xl">
      {/* Back */}
      <button onClick={() => navigate("/admin/partners")}
        className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-4">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Partners
      </button>

      {err && <div className="mb-4 px-4 py-3 bg-red-950/80 border border-red-800/60 rounded-lg text-red-200 text-sm">{err}</div>}

      {/* Profile card */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-2xl overflow-hidden">
        {/* Cover */}
        <div className="h-40 bg-gray-800 relative">
          {banner ? (
            <img src={String(banner)} alt="cover" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-indigo-900/40 via-gray-800 to-cyan-900/30" />
          )}
        </div>

        {/* Header row */}
        <div className="px-6 pb-5 relative z-10">
          <div className="flex items-end justify-between -mt-12 mb-4">
            <div className="flex items-end gap-4 min-w-0">
              {/* Circle logo */}
              <div className="w-24 h-24 rounded-full bg-gray-800 border-4 border-gray-900 overflow-hidden flex items-center justify-center shrink-0">
                {logo ? (
                  <img src={String(logo)} alt={orgName} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold text-gray-500">{orgName[0]?.toUpperCase()}</span>
                )}
              </div>
              <div className="min-w-0 pb-1">
                <h1 className="text-xl font-semibold text-white truncate">{brandName || orgName}</h1>
                {brandName && <p className="text-sm text-gray-500 truncate">{orgName}</p>}
                {tagline && <p className="text-sm text-gray-400 truncate mt-0.5">{tagline}</p>}
              </div>
            </div>
            {!loading && (
              <div className="flex items-center gap-2.5 shrink-0">
                <RefreshButton onRefresh={load} />
                <button onClick={() => {
                  setEditing((v) => {
                    if (v) void load(); // leaving edit mode: refresh header (org name, etc.)
                    return !v;
                  });
                }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors shrink-0 ${
                    editing ? "bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700"
                            : "bg-indigo-600 hover:bg-indigo-500 text-white"
                  }`}>
                  {editing ? "Done editing" : "Edit profile"}
                </button>
              </div>
            )}
          </div>

          {/* Meta badges */}
          <div className="flex flex-wrap items-center gap-2">
            {ptype && (
              <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-800 text-gray-300 border border-gray-700 capitalize">
                {typeLabel(ptype)}
              </span>
            )}
            {meta && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ${
                meta.is_verified ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"
              }`}>
                <span className={`w-1 h-1 rounded-full ${meta.is_verified ? "bg-emerald-400" : "bg-amber-400"}`} />
                {meta.is_verified ? "Verified" : "Pending verification"}
              </span>
            )}
            {q?.status && (
              <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${
                q.status.completed ? "bg-emerald-500/10 text-emerald-400" : "bg-gray-800 text-gray-400 border border-gray-700"
              }`}>
                {q.status.completed ? "Onboarding complete" : `${q.status.missing_steps?.length || 0} steps left`}
              </span>
            )}
            {meta && meta.status !== "active" && (
              <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-red-500/10 text-red-400 capitalize">{meta.status}</span>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="mt-6 space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-32 bg-gray-900/40 border border-gray-800 rounded-xl animate-pulse" />)}
        </div>
      ) : editing ? (
        <div className="mt-6 bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
          <h2 className="text-sm font-semibold text-white mb-1">Edit profile</h2>
          <p className="text-xs text-gray-500 mb-2">Changes save per section. Close editing to see the updated profile.</p>
          <PartnerOnboardingEditor ownerId={ownerId} />
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {sections.map((s) => (
            <div key={s.key} className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
              <h2 className="text-sm font-semibold text-white mb-4">{s.label}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                {s.fields.map((f) => (
                  <div key={f.key} className={f.type === "textarea" || f.type === "media_list" ? "md:col-span-2" : ""}>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-1.5">{f.label}</div>
                    <ReadValue f={f} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {sections.length === 0 && (
            <div className="bg-gray-900/40 border border-dashed border-gray-800 rounded-2xl px-6 py-12 text-center">
              <p className="text-gray-400 text-sm">No profile details yet.</p>
              <p className="text-gray-600 text-xs mt-1">Click “Edit profile” to complete onboarding.</p>
            </div>
          )}
        </div>
      )}

      {/* Showcase media — below the channel profile, always visible regardless of the edit toggle */}
      {!loading && (
        <div className="mt-6">
          <PartnerShowcaseEditor ownerId={ownerId} />
        </div>
      )}
    </div>
  );
}
